/**
 * @fileoverview Hook for proactive token refresh based on UI activity.
 *
 * This hook monitors token expiration and automatically refreshes the token
 * before it expires while the user is active in the UI.
 *
 * Behavior:
 * - Checks token expiration every 30 seconds
 * - Only refreshes when user is active (not idle)
 * - Uses dedicated /auth/refresh endpoint for refresh
 * - Token refresh happens automatically via responseTransform when X-Refreshed-JWT header is received
 * - Refresh threshold: 1 minute before expiration (fixed)
 * - Check interval: 30 seconds (fixed)
 *
 * Usage:
 * - Should be used within AutoLogoutProvider to track UI activity
 * - Automatically handles token refresh without user interaction
 */

import {useCallback, useEffect, useRef} from "react";
import {select, store} from "../store";
import {shouldRefreshToken} from "../utils/token";
import {history} from "../utils/history";
import {http} from "../services/api/restApiClient";
import {useAutoLogout} from "../contexts/AutoLogoutContext";

/**
 * Configuration options for proactive token refresh.
 */
interface UseProactiveTokenRefreshOptions {
  /**
   * Threshold in milliseconds before expiration to trigger refresh.
   * Default: 1 minute (60,000 ms)
   */
  refreshThresholdMs?: number;
  /**
   * Interval in milliseconds to check token expiration.
   * Default: 30 seconds (30,000 ms)
   */
  checkIntervalMs?: number;
}

/**
 * URL for proactive token refresh (works for both USER and ADMISSION tokens).
 */
const PROACTIVE_TOKEN_REFRESH_URL = "/auth/refresh";

/**
 * Hook for proactive token refresh based on UI activity.
 *
 * @param {UseProactiveTokenRefreshOptions} options Configuration options.
 */
export const useProactiveTokenRefresh = ({
  refreshThresholdMs = 60 * 1000, // Default: 1 minute
  checkIntervalMs = 30 * 1000, // Default: 30 seconds
}: UseProactiveTokenRefreshOptions = {}) => {
  const {getRemainingTime} = useAutoLogout();
  const refreshInProgressRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const checkAndRefreshTokenRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      // Don't refresh if already refreshing
      if (refreshInProgressRef.current) {
        return;
      }

      // Get current token from Redux store based on route (same rule as getCommonAuthorizationHeader)
      const state = store.getState();
      const token = history.location.pathname.startsWith("/admin")
        ? select.adminSession.getToken(state)
        : select.admissionSession.getToken(state);

      // Don't refresh if no token (user not logged in)
      if (!token) {
        return;
      }

      // Use fixed threshold (1 minute by default)
      const threshold = refreshThresholdMs;

      // Check if token needs refresh
      if (!shouldRefreshToken(token, threshold)) {
        return;
      }

      // Refresh token by calling lightweight endpoint
      // The responseTransform will automatically extract X-Refreshed-JWT header
      // and call setToken to update the token in Redux store
      refreshInProgressRef.current = true;
      try {
        await http.get(PROACTIVE_TOKEN_REFRESH_URL);
      } catch (error) {
        // Error handling is done by responseTransform
        // If token is expired, it will trigger logout
        // We just need to reset the flag
      } finally {
        refreshInProgressRef.current = false;
      }
    };

    // Store function reference so it can be called from outside
    checkAndRefreshTokenRef.current = checkAndRefreshToken;

    // Use fixed interval (30 seconds by default)
    const interval = checkIntervalMs;

    // Initial check
    checkAndRefreshToken();

    // Set up interval to check periodically
    intervalRef.current = setInterval(checkAndRefreshToken, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      checkAndRefreshTokenRef.current = null;
    };
  }, [refreshThresholdMs, checkIntervalMs, getRemainingTime]);

  // Return a stable callable that invokes the ref when called. We don't return
  // checkAndRefreshTokenRef.current because that is only set in useEffect (after
  // render), so it would be null on first render and break on-demand "refresh on
  // becoming active" until a re-render. This way the consumer always gets a
  // function that, when called, runs whatever is currently in the ref.
  return useCallback(() => {
    checkAndRefreshTokenRef.current?.();
  }, []);
};
